import { Button } from '@/components/ui/button';
import { ProjectSelector } from './ProjectSelector';
import { UserMenu } from './UserMenu';
import { useSettingsStore } from '@/store/settingsStore';
import { useProjects, useContextSwitcher } from '@/features/organizations/hooks';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';

/**
 * Topbar Component
 * 
 * Simplified top navigation bar following the original Nuxt dashboard structure
 * with enhanced accessibility features.
 * 
 * Responsibilities:
 * - Display logo with proper alt text
 * - Display project selector (when projects available)
 * - Show user menu with organization selector and settings
 * - Handle mobile menu toggle with keyboard support
 * - Provide proper ARIA labels and roles
 */

interface TopbarProps {
  onMenuToggle?: () => void;
  isContextLoading?: boolean;
}

export function Topbar({ onMenuToggle, isContextLoading = false }: TopbarProps) {
  const { t } = useTranslation();
  const { selectedProject, projects } = useSettingsStore();
  const { isLoading: isLoadingProjects } = useProjects();
  const { switchProject } = useContextSwitcher();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-2 sm:px-4 lg:px-6">
      {/* Mobile menu toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden mr-2 sm:mr-4 h-9 w-9 flex-shrink-0"
        onClick={onMenuToggle}
        aria-label={t('accessibility.toggleNavigationMenu', { defaultValue: 'Toggle navigation menu' })}
        aria-expanded={false} // This would need to be passed as prop if we track sidebar state
        aria-controls="navigation"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </Button>

      {/* Logo - visible on mobile and tablets, hidden on desktop (sidebar shows logo) */}
      <div className="flex items-center lg:hidden flex-shrink-0">
        <Link
          to={ROUTES.DASHBOARD}
          className="inline-flex items-center gap-2 sm:gap-3 transition-opacity hover:opacity-80"
          aria-label={t('accessibility.goToDashboard', { defaultValue: 'Go to dashboard' })}
        >
          <img
            src="/logo_wya.png"
            alt={t('accessibility.companyLogo', { defaultValue: 'WYA Company Logo' })}
            className="h-5 sm:h-6 w-auto max-w-[60px] sm:max-w-[80px]"
          />
        </Link>
      </div>

      {/* Spacer */}
      <div className="flex-1 min-w-0" />

      {/* Right side - Project Selector and User Menu */}
      <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
        {/* Project Selector (only if projects exist) */}
        {projects.length > 0 && (
          <div role="region" aria-label={t('accessibility.projectSelection', { defaultValue: 'Project selection' })}>
            <ProjectSelector
              projects={projects}
              selectedProject={selectedProject}
              onProjectChange={switchProject}
              loading={isLoadingProjects || isContextLoading}
            />
          </div>
        )}
        
        {/* User Menu */}
        <div role="region" aria-label={t('accessibility.userMenu', { defaultValue: 'User menu' })}>
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
